import { ExperimentationClient } from 'experimentation-client-js';
import { SubjectAttributes } from 'experimentation-client-js';
import { useCallback, useEffect, useState } from 'react';

import { useExperimentation } from './useExperimentation';

/**
 * Configuration for the useFeatureFlag hook.
 */
interface UseFeatureFlagOptions<T> {
    /**
     * The feature flag key to evaluate.
     */
    flagKey: string;

    /**
     * Subject attributes for targeting evaluation.
     * If not provided, uses the attributes from ExperimentationProvider.
     */
    subjectAttributes?: SubjectAttributes;

    /**
     * Default value if flag evaluation fails or client is not ready.
     */
    defaultValue: T;

    /**
     * The type of flag to evaluate.
     * @default 'boolean'
     */
    type?: 'boolean' | 'string' | 'number' | 'integer' | 'json';
}

/**
 * Hook for evaluating a feature flag.
 * Automatically handles loading states and returns the appropriate value.
 *
 * @example
 * ```tsx
 * // Boolean flag
 * const { value: showNewUI, isReady } = useFeatureFlag({
 *   flagKey: 'new-ui-redesign',
 *   defaultValue: false,
 *   type: 'boolean',
 * });
 *
 * // JSON flag with type safety - explicitly type the generic
 * type DownloadPageConfig = {
 *   title: string;
 *   title_color: string;
 * };
 * const { value, isReady } = useFeatureFlag<DownloadPageConfig>({
 *   flagKey: 'download-page-config',
 *   defaultValue: { title: 'Default', title_color: 'text-main' },
 *   type: 'json',
 * });
 * // Now you can access: value.title, value.title_color with type safety
 * ```
 */
export function useFeatureFlag<T extends boolean | string | number | object = boolean>(
    options: UseFeatureFlagOptions<T>
): { value: T; isReady: boolean } {
    const { flagKey, subjectAttributes: subjectAttributesOverride, defaultValue, type = 'boolean' } = options;
    const { syncClient, asyncClient, subjectAttributes: contextSubjectAttributes } = useExperimentation();

    const subjectAttributes = subjectAttributesOverride
        ? ({
              ...contextSubjectAttributes,
              ...subjectAttributesOverride,
              extras: {
                  ...contextSubjectAttributes.extras,
                  ...subjectAttributesOverride.extras,
              },
          } as SubjectAttributes)
        : contextSubjectAttributes;

    const evaluate = useCallback(
        (client: ExperimentationClient | null): T => {
            if (!client) return defaultValue;

            try {
                switch (type) {
                    case 'boolean':
                        return client.getBooleanAssignment(flagKey, subjectAttributes, defaultValue as boolean) as T;
                    case 'string':
                        return client.getStringAssignment(flagKey, subjectAttributes, defaultValue as string) as T;
                    case 'number':
                        return client.getNumericAssignment(flagKey, subjectAttributes, defaultValue as number) as T;
                    case 'integer':
                        return client.getIntegerAssignment(flagKey, subjectAttributes, defaultValue as number) as T;
                    case 'json':
                        return client.getJSONAssignment(flagKey, subjectAttributes, defaultValue as object) as T;
                    default:
                        return defaultValue;
                }
            } catch {
                return defaultValue;
            }
        },
        [flagKey, subjectAttributes, defaultValue, type]
    );

    // evaluate is defined before useState, so it's safe to use here
    const [value, setValue] = useState<T>(() => evaluate(syncClient));
    const [isReady, setIsReady] = useState(syncClient !== null);

    useEffect(() => {
        if (syncClient) return;

        (async () => {
            const client = await asyncClient;
            setValue(evaluate(client));
            setIsReady(true);
        })();
    }, [asyncClient, evaluate]);

    return { value, isReady };
}
